import { Link } from 'react-router-dom'
import { FileQuestion } from 'lucide-react'

interface DocNotFoundProps {
  slug: string
}

/**
 * Shown when a slug does not match any known doc entry.
 * Replaces the previous silent redirect to / so operators and integrators
 * get a clear signal that the page does not exist.
 *
 * W3: focus is moved to #main-content by useFocusMain in the parent page
 * components, so screen readers announce the heading naturally on SPA
 * navigation. We use a real <h2> rather than aria-live so the announcement
 * is reliable on fresh mount (aria-live only announces *changes* to
 * already-mounted elements).
 */
export function DocNotFound({ slug }: DocNotFoundProps) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-md border border-border bg-muted/30 px-8 py-12 text-center">
      <FileQuestion size={36} aria-hidden className="text-muted-fg" />
      <div>
        <h2 className="text-base font-semibold text-fg">Page not found</h2>
        <p className="mt-1 text-sm text-muted-fg">
          No documentation page matches{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-xs font-mono">{slug}</code>.
        </p>
      </div>
      <Link
        to="/developers/onboarding"
        className="mt-2 inline-flex h-8 items-center rounded border border-border bg-bg px-3 text-xs font-medium text-fg transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
      >
        Back to overview
      </Link>
    </div>
  )
}
